import { Fragment, useEffect, useMemo, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  addMonths,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toIsoMonth = (date) => format(date, "yyyy-MM");

const safeParseIsoMonth = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    // Parse YYYY-MM format
    const match = value.match(/^(\d{4})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (year < 1900 || year > 2100 || month < 1 || month > 12) return null;
    const d = new Date(year, month - 1, 1);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
};

export const MonthPickerInput = ({
  value,
  onChange,
  placeholder = "Pick a month",
  disabled = false,
  min,
  max,
  className = "",
  buttonClassName = "",
}) => {
  const today = useMemo(() => new Date(), []);
  const selectedMonth = useMemo(() => safeParseIsoMonth(value), [value]);
  const minMonth = useMemo(() => safeParseIsoMonth(min), [min]);
  const maxMonth = useMemo(() => safeParseIsoMonth(max), [max]);

  const [visibleYear, setVisibleYear] = useState(() => {
    if (selectedMonth) return selectedMonth.getFullYear();
    return today.getFullYear();
  });

  useEffect(() => {
    if (selectedMonth) {
      setVisibleYear(selectedMonth.getFullYear());
    }
  }, [selectedMonth]);

  const isDisabledMonth = (year, monthIndex) => {
    const monthDate = new Date(year, monthIndex, 1);
    if (minMonth && isBefore(monthDate, startOfMonth(minMonth))) return true;
    if (maxMonth && isAfter(monthDate, startOfMonth(maxMonth))) return true;
    return false;
  };

  const selectMonth = (year, monthIndex, close) => {
    if (disabled) return;
    if (isDisabledMonth(year, monthIndex)) return;
    const monthDate = new Date(year, monthIndex, 1);
    onChange?.(toIsoMonth(monthDate));
    close?.();
  };

  const clear = (close) => {
    if (disabled) return;
    onChange?.("");
    close?.();
  };

  const goCurrentMonth = (close) => {
    if (disabled) return;
    onChange?.(toIsoMonth(today));
    close?.();
  };

  const label = selectedMonth
    ? `${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`
    : placeholder;

  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();

  return (
    <Popover className={`relative ${className}`}>
      {({ close }) => (
        <>
          <Popover.Button
            disabled={disabled}
            className={[
              "flex w-full items-center justify-start gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm",
              "outline-none",
              disabled ? "cursor-not-allowed opacity-60" : "",
              buttonClassName,
            ].join(" ")}
          >
            <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
            <span className={selectedMonth ? "text-gray-900" : "text-gray-500"}>{label}</span>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-30 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
              <div className="flex items-center justify-between pb-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
                  onClick={() => setVisibleYear((y) => y - 1)}
                  disabled={disabled}
                >
                  <ChevronLeftIcon className="h-4 w-4 text-gray-700" />
                </button>
                <div className="text-sm font-semibold text-gray-900">{visibleYear}</div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
                  onClick={() => setVisibleYear((y) => y + 1)}
                  disabled={disabled}
                >
                  <ChevronRightIcon className="h-4 w-4 text-gray-700" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {MONTHS.map((monthName, monthIndex) => {
                  const monthDate = new Date(visibleYear, monthIndex, 1);
                  const isSelected =
                    selectedMonth &&
                    selectedMonth.getFullYear() === visibleYear &&
                    selectedMonth.getMonth() === monthIndex;
                  const isCurrent =
                    currentYear === visibleYear && currentMonthIndex === monthIndex;
                  const monthDisabled = disabled || isDisabledMonth(visibleYear, monthIndex);

                  return (
                    <button
                      key={monthName}
                      type="button"
                      disabled={monthDisabled}
                      onClick={() => selectMonth(visibleYear, monthIndex, close)}
                      className={[
                        "inline-flex h-12 items-center justify-center rounded-md text-sm font-medium transition-colors",
                        isSelected
                          ? "bg-gray-900 text-white"
                          : "text-gray-900 hover:bg-gray-100",
                        !isSelected && isCurrent ? "ring-1 ring-gray-300" : "",
                        monthDisabled ? "cursor-not-allowed opacity-60" : "",
                      ].join(" ")}
                    >
                      {monthName.slice(0, 3)}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  onClick={() => clear(close)}
                  disabled={disabled}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  onClick={() => goCurrentMonth(close)}
                  disabled={disabled}
                >
                  Current Month
                </button>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
